import { Stack } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Union } from 'ts-toolbelt';

import { DataGridProStyled } from '../../../components/AwardingDataGrid/AwardingDataGrid.styled';
import { I18nNamespace } from '~/common/types';
import { CircularProgressCentered } from '~/components/CircularProgressCentered/CircularProgressCentered';
import { MaskedBiddingAlert } from '~/components/MaskedBiddingAlert/MaskedBiddingAlert';
import { LineItemNegotiationCaseAssignments } from '~/modules/DemandAwardingWidget/DemandAwardingWidget.hooks';
import {
  Demand,
  DemandAwardingData,
  DemandAwardingSettings,
  DemandAwardingStatus,
} from '~/modules/DemandAwardingWidget/DemandAwardingWidget.types';

import { useAwardingDataGridLineItemBiddingConfig } from './AwardingDataGridLineItemBidding.config';

export interface AwardingDataGridLineItemBiddingProps {
  negotiationCases?: Union.Nullable<DemandAwardingData[]>;
  demand?: Union.Nullable<Demand>;
  awardingSettings?: Union.Nullable<DemandAwardingSettings>;
  status?: Union.Nullable<DemandAwardingStatus[]>;
  awardableConfirmedQuotes: boolean;
  isMaskedBidding?: boolean;
  loading?: boolean;
  lineItemAssignments: LineItemNegotiationCaseAssignments;
}

export const AwardingDataGridLineItemBidding = ({
  negotiationCases,
  awardingSettings,
  status,
  awardableConfirmedQuotes,
  isMaskedBidding,
  loading,
  demand,
  lineItemAssignments,
}: AwardingDataGridLineItemBiddingProps) => {
  const gridProps = useAwardingDataGridLineItemBiddingConfig({
    negotiationCases,
    status,
    awardingSettings,
    demand,
    awardableConfirmedQuotes,
    lineItemAssignments,
  });
  const { t: tCommon } = useTranslation(I18nNamespace.common, { keyPrefix: 'CompetitiveBidding' });

  // Show spinner while data is retrieved to avoid blips of empty grid in masked bidding mode
  if (loading) {
    return (
      <Stack position="relative" sx={{ height: '100px' }}>
        <CircularProgressCentered />
      </Stack>
    );
  }

  return isMaskedBidding ? (
    <MaskedBiddingAlert message={tCommon('awarding')} sx={{ margin: 4 }} />
  ) : (
    <DataGridProStyled
      treeData
      autoHeight
      loading={loading}
      hideFooter
      disableColumnMenu
      getRowHeight={() => 'auto'}
      defaultGroupingExpansionDepth={2}
      {...gridProps}
    />
  );
};
